// ============================================================
// FILE: _services/ecg-standalone11/ecg-standalone11-manager.service.ts
//
// Ported from EcgConnectStandalone11's src/app/ecg/web-serial-ecg.service.ts
// (WebSerialEcgService), following the same porting convention already used
// for this app's other device managers (x6-manager.service.ts, fetal-
// doppler-manager.service.ts): the connection lifecycle, retry/timeout
// handling and command protocol are kept unchanged (do not "clean up"
// without re-verifying against real hardware). Two adaptations for this
// app:
//   - Ecg11State lives in the shared device-integration model file (this
//     app's convention), not declared inline; LogCaptureService (a
//     dev-tool the standalone app has but this app doesn't) is dropped in
//     favor of plain console logging, matching every other manager here.
//   - sample$ emits ready-to-plot Leads12 (post Ecg11LiveProcessor DSP),
//     not raw per-channel frames -- the DSP pipeline runs INSIDE this
//     service so every consumer (waveform component, guided capture) gets
//     the same already-filtered/baseline-corrected 12-lead values, exactly
//     like the reference app's app.component.ts did per-consumer.
//
// Also ported: the stream watchdog (STREAM_WATCHDOG_TIMEOUT_MS) added to
// the reference app to fix the Windows-specific symptom where a stale
// Bluetooth pairing lets the port "connect" and accept commands but never
// deliver data. It's purely additive diagnostics/recovery around the
// existing connect/stream/teardown calls -- no change to the connection
// lifecycle itself, so Mac's already-working path is unaffected.
//
// Uses navigator.serial (Web Serial), NOT navigator.bluetooth -- this
// device is classic Bluetooth SPP, which Web Bluetooth's GATT-only API
// cannot reach at all. This keeps it structurally isolated from X6/Fetal
// Doppler's navigator.bluetooth usage and Aabo Scope's WebSocket bridge:
// there is no shared connection object, event target or state any of them
// could accidentally clobber.
// ============================================================
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Ecg11FrameParser } from './ecg-standalone11-frame-parser';
import { computeLeadOffFromStatus, Ecg11LiveProcessor } from './ecg-standalone11-signal-processor';
import { Ecg11State, Leads12 } from '../../@core/interfaces/device-integration/device-item.model';

const CMD_STOP = 'S';
const CMD_RESET = 'R';
const CMD_START = 'A';
const ACK_OK = 'y';
const ACK_FAIL = 'x';

const COMMAND_NAMES: Record<string, string> = { [CMD_STOP]: 'STOP', [CMD_RESET]: 'RESET', [CMD_START]: 'START ACQUISITION' };

const INITIAL_STATE: Ecg11State = {
  supported: typeof navigator !== 'undefined' && 'serial' in navigator,
  connection: 'disconnected',
  isStreaming: false,
  warmedUp: false,
  errorMessage: null,
  batteryPercent: null,
  knownPortCount: 0,
  leadOff: {},
};

const CONNECT_RETRY_ATTEMPTS = 3;
const CONNECT_RETRY_DELAY_MS = 1000;
// The OS can fail to respond to an open() call at all rather than
// rejecting it, which without a timeout would leave the UI stuck on
// "connecting" forever.
const CONNECT_OPEN_TIMEOUT_MS = 8000;
// On Windows, a stale Bluetooth pairing for this device can accept the
// serial connection and every command without error, yet never actually
// deliver data -- the port looks "connected" forever with nothing arriving.
// This watchdog is what actually notices and recovers from that (Mac's own
// pairing doesn't exhibit it, but the watchdog runs unconditionally since it
// only fires when data has genuinely stopped).
const STREAM_WATCHDOG_TIMEOUT_MS = 8000;
// Before the hard watchdog above gives up and disconnects, try to revive a
// stalled stream in place (STOP/RESET/START, fresh parser + ACK queue) -- a
// stall is often just a lost START or a desynced link, and disconnecting
// leaves the caller staring at a dead trace until they reconnect by hand.
const SOFT_RESTART_AFTER_MS = 3000;
const SOFT_RESTART_RETRY_GAP_MS = 2500;
const MAX_SOFT_RESTARTS = 2;
// Web Serial's BufferOverrunError / BreakError / FramingError / ParityError
// are non-fatal per spec (a fresh port.readable + reader is expected) -- only
// give up on the connection if errors repeat back-to-back with no data
// between them.
const MAX_CONSECUTIVE_READ_ERRORS = 5;
const WRITE_TIMEOUT_MS = 5000;
const SIGNALS_TIMEOUT_MS = 5000;
const CLOSE_TIMEOUT_MS = 5000;
// Battery is only reported inside a full (0xAA) frame's status bytes, and
// the device only sends frames while acquiring -- so right after connect a
// brief START -> first-battery-reading -> STOP probe runs, otherwise the
// Device Integration card shows no battery until an ECG is captured.
const BATTERY_PROBE_TIMEOUT_MS = 4000;
// Proactive re-arm -- ported from the Angular 9 reference app's "Re-arm every
// 3 s" (rearm3) test mode, the setting confirmed on real hardware to keep the
// trace continuous. The device stops sending by itself ~11.8-13.3s after
// every START; a bare START sent while it's still acquiring restarts that
// counter, so re-arming every 3s means the stop never happens. Only sent
// while frames are actually flowing -- once the stream is silent the
// soft-restart/watchdog path below owns recovery, unchanged.
const PROACTIVE_REARM_MS = 3000;

// port.open() already had a timeout (openWithTimeout below) -- write()/
// setSignals()/close() didn't. A stale Bluetooth-serial pairing can accept
// the port open with no error and then just never resolve any of those, so
// wrapping each OS-level call in a bounded race turns a silent infinite hang
// into a clear, recoverable error instead of e.g. permanently blocking every
// future connect() attempt with "Already attempting a connection".
function withTimeout<T>(promise: Promise<T>, ms: number, timeoutMessage: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(timeoutMessage)), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); }
    );
  });
}

@Injectable({ providedIn: 'root' })
export class Ecg11ManagerService {
  private readonly stateSubject = new BehaviorSubject<Ecg11State>(INITIAL_STATE);
  readonly state$: Observable<Ecg11State> = this.stateSubject.asObservable();

  /** Ready-to-plot 12-lead samples, post-DSP -- null while warming up (nothing to plot yet). */
  private readonly sampleSubject = new Subject<Leads12>();
  readonly sample$: Observable<Leads12> = this.sampleSubject.asObservable();

  private readonly processor = new Ecg11LiveProcessor();

  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  // Acquired ONCE and reused for every write for the life of the
  // connection -- acquiring a fresh writer per write (including the
  // ~500/sec per-frame ACK bytes) throws "Cannot create writer when
  // WritableStream is locked" once frames start arriving that fast.
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private parser: Ecg11FrameParser | null = null;

  private isConnecting = false;
  private connectAttemptToken = 0;
  private streamAttemptToken = 0;
  private isStreamingFlag = false;
  private batteryProbeActive = false;

  // Stream watchdog / diagnostics -- see STREAM_WATCHDOG_TIMEOUT_MS above.
  private activityInterval: any = null;
  private framesThisWindow = 0;
  private nacksThisWindow = 0;
  private bytesThisWindow = 0;
  private hasLoggedFirstFrame = false;
  private hasLoggedFirstNack = false;
  private hasLoggedFirstBytes = false;
  private consecutiveZeroByteWindows = 0;
  private hasLoggedZeroByteHint = false;
  private lastValidFrameAtMs = 0;
  private softRestartAttempts = 0;
  private lastSoftRestartAtMs = 0;
  private softRestartInFlight = false;

  // Proactive re-arm -- see PROACTIVE_REARM_MS above.
  private runStartedAtMs = 0;
  private lastProactiveRearmAtMs = 0;
  private proactiveRearmInFlight = false;
  // While > 0, per-frame ACK bytes are dropped so no 'y'/'x' byte can
  // interleave with a command write (same as the reference app). A refcount,
  // not a boolean, so overlapping bursts can't re-enable ACKs for each other.
  private ackSuppressDepth = 0;

  constructor() {
    if (INITIAL_STATE.supported) {
      this.refreshKnownPortCount();
      navigator.serial.addEventListener('disconnect', (event: Event) => {
        if ((event as any).target === this.port) {
          console.warn('[Ecg11Manager] Device disconnected (unplugged, or out of Bluetooth range)');
          this.teardown();
        }
      });
    } else {
      console.warn('[Ecg11Manager] This browser does not support Web Serial (navigator.serial) -- use Chrome or Edge.');
    }
  }

  private async refreshKnownPortCount(): Promise<void> {
    try {
      const ports = await navigator.serial.getPorts();
      this.setState({ ...this.stateSubject.value, knownPortCount: ports.length });
    } catch {
      // non-fatal -- just means a "reconnect known device" affordance stays hidden
    }
  }

  /** Opens the browser's native "select a serial port" permission dialog. Must run synchronously from a user gesture (button click). */
  async connect(): Promise<void> {
    if (!this.stateSubject.value.supported) {
      this.setError('This browser does not support Web Serial. Use Chrome or Edge.');
      return;
    }
    if (this.isConnecting) {
      this.setError('Already attempting a connection -- wait for it to finish or fail.');
      return;
    }
    // Set synchronously, BEFORE the requestPort() await -- requestPort() can
    // take as long as the user takes to decide, and the isConnecting guard
    // above must already be true for the whole of that wait.
    this.isConnecting = true;
    this.setState({ ...this.stateSubject.value, connection: 'connecting', errorMessage: null });
    let port: SerialPort;
    try {
      port = await navigator.serial.requestPort();
    } catch (e) {
      this.isConnecting = false;
      if (e instanceof DOMException && e.name === 'NotFoundError') {
        // User cancelled the picker -- not a real error.
        this.setState({ ...this.stateSubject.value, connection: 'disconnected' });
        return;
      }
      this.setState({ ...this.stateSubject.value, connection: 'disconnected' });
      this.setError(`Could not open the device picker: ${this.describeError(e)}`);
      return;
    }
    await this.connectToPort(port);
  }

  private async connectToPort(port: SerialPort): Promise<void> {
    if (this.port) await this.teardown();

    this.isConnecting = true;
    const myToken = ++this.connectAttemptToken;
    let lastErr: any;
    try {
      for (let attempt = 1; attempt <= CONNECT_RETRY_ATTEMPTS; attempt++) {
        if (myToken !== this.connectAttemptToken) {
          this.setState({ ...this.stateSubject.value, connection: 'disconnected' });
          return; // superseded by a disconnect/newer connect attempt
        }
        try {
          await this.openWithTimeout(port, CONNECT_OPEN_TIMEOUT_MS);

          if (myToken !== this.connectAttemptToken) {
            try { await port.close(); } catch { /* best-effort */ }
            this.setState({ ...this.stateSubject.value, connection: 'disconnected' });
            return;
          }

          this.port = port;
          if (port.writable) {
            this.writer = port.writable.getWriter();
          }

          // Some serial devices (including some Bluetooth-SPP-to-serial
          // bridges) won't send anything until DTR is asserted -- non-fatal
          // if the platform/device doesn't support signal control at all.
          try {
            await withTimeout(
              port.setSignals({ dataTerminalReady: true, requestToSend: true }),
              SIGNALS_TIMEOUT_MS,
              `setSignals timed out after ${SIGNALS_TIMEOUT_MS}ms`
            );
          } catch (e) {
            console.warn('[Ecg11Manager] setSignals (DTR/RTS) not supported or failed:', this.describeError(e));
          }

          this.setState({ ...this.stateSubject.value, connection: 'connected', errorMessage: null });
          console.log('[Ecg11Manager] Connected to device');
          this.refreshKnownPortCount();

          this.parser = new Ecg11FrameParser(
            (frame) => {
              if (this.batteryProbeActive && !this.isStreamingFlag) {
                // Probe only -- read battery, don't feed the DSP/lead-off UI.
                if (frame.status) this.updateBattery(frame.status.status3);
                return;
              }
              if (this.isStreamingFlag) {
                this.lastValidFrameAtMs = Date.now();
                this.softRestartAttempts = 0;
                this.framesThisWindow++;
                if (!this.hasLoggedFirstFrame) {
                  this.hasLoggedFirstFrame = true;
                  console.log('[Ecg11Manager] <- first frame received -- device is responding');
                }
              }
              const leadOff = frame.status ? computeLeadOffFromStatus(frame.status) : this.stateSubject.value.leadOff;
              if (frame.status) this.updateBattery(frame.status.status3);
              if (leadOff !== this.stateSubject.value.leadOff) {
                this.setState({ ...this.stateSubject.value, leadOff });
              }
              const leads = this.processor.processSample(frame.rawChannels);
              if (leads) {
                if (!this.stateSubject.value.warmedUp) {
                  this.setState({ ...this.stateSubject.value, warmedUp: true });
                }
                this.sampleSubject.next(leads);
              }
            },
            (ok) => {
              if (this.batteryProbeActive && !this.isStreamingFlag) {
                this.queueAck(ok ? ACK_OK : ACK_FAIL);
                return;
              }
              if (!this.isStreamingFlag) return;
              if (!ok) {
                this.nacksThisWindow++;
                if (!this.hasLoggedFirstNack) {
                  this.hasLoggedFirstNack = true;
                  console.warn('[Ecg11Manager] <- first NACK sent -- a frame failed its end-of-frame check');
                }
              }
              this.queueAck(ok ? ACK_OK : ACK_FAIL);
            }
          );

          this.startReadLoop(port);

          // Matches the vendor app's connect sequence: stop any previous
          // acquisition, then reset, before startStreaming() explicitly
          // starts it.
          await this.writeCommand(CMD_STOP);
          await this.writeCommand(CMD_RESET);
          void this.probeBattery();
          return;
        } catch (err) {
          lastErr = err;
          console.warn(`[Ecg11Manager] Open attempt ${attempt}/${CONNECT_RETRY_ATTEMPTS} failed:`, this.describeError(err));

          // A timed-out open() never actually settled at the browser/OS
          // level and Web Serial has no way to abort it -- retrying on the
          // SAME port object throws InvalidStateError instantly, wasting
          // the whole retry budget on a guaranteed failure.
          if ((err as any)?.isOpenTimeout) break;

          if (attempt < CONNECT_RETRY_ATTEMPTS) {
            await this.sleep(CONNECT_RETRY_DELAY_MS);
          }
        }
      }
      this.setState({ ...this.stateSubject.value, connection: 'disconnected' });
      this.setError(this.friendlyConnectError(lastErr));
    } finally {
      if (myToken === this.connectAttemptToken) {
        this.isConnecting = false;
      }
    }
  }

  private openWithTimeout(port: SerialPort, ms: number): Promise<void> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        const err: any = new Error(`Timed out after ${ms}ms waiting for the OS to open this port. Reload this page before trying again.`);
        err.isOpenTimeout = true;
        reject(err);
      }, ms);
      // Web Serial's default 255-byte receive buffer overflows (silently
      // dropping bytes -> frame desync) whenever the main thread is briefly
      // busy; this stream is ~10 KB/s, so give it real headroom.
      port.open({ baudRate: 115200, bufferSize: 65536 }).then(
        () => {
          if (settled) {
            port.close().catch(() => {});
            return;
          }
          settled = true;
          clearTimeout(timer);
          resolve();
        },
        (err) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(err);
        }
      );
    });
  }

  private async startReadLoop(port: SerialPort): Promise<void> {
    if (!port.readable) return;
    let consecutiveErrors = 0;
    try {
      // Web Serial's BufferOverrunError / BreakError / FramingError /
      // ParityError are NON-fatal: the spec swaps in a fresh port.readable
      // and expects the page to take a new reader. Treating any read error
      // as fatal means a single Bluetooth glitch or a briefly busy main
      // thread tears the whole connection down and freezes the trace. Only
      // give up when the port has really gone (readable becomes null), the
      // port was replaced, or errors repeat without any data.
      while (port.readable && this.port === port && consecutiveErrors < MAX_CONSECUTIVE_READ_ERRORS) {
        const reader = port.readable.getReader();
        this.reader = reader;
        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) return;
            if (value) {
              consecutiveErrors = 0;
              this.bytesThisWindow += value.length;
              if (!this.hasLoggedFirstBytes) {
                this.hasLoggedFirstBytes = true;
                const hex = Array.from(value.slice(0, 32)).map(b => b.toString(16).padStart(2, '0')).join(' ');
                console.log(`[Ecg11Manager] <- first bytes received (${value.length} bytes): ${hex}${value.length > 32 ? '...' : ''}`);
              }
              if (this.parser) this.parser.feed(value);
            }
          }
        } catch (e) {
          consecutiveErrors++;
          console.warn(`[Ecg11Manager] Read error (${consecutiveErrors}/${MAX_CONSECUTIVE_READ_ERRORS}):`, this.describeError(e), '-- re-acquiring the reader');
          this.parser?.reset();
        } finally {
          try { reader.releaseLock(); } catch { /* already released */ }
        }
      }
    } finally {
      if (this.port === port) await this.teardown();
    }
  }

  // FIX ("ECG line runs, then gets stuck"): the device needs an ACK byte for
  // every frame, ~500/s. A single write() per frame plus this connection's
  // WRITE_TIMEOUT_MS-guarded await can easily take longer than the ~2ms
  // budget that 500/s allows, and a WritableStream queues writes without
  // bound -- so the ACK backlog would grow every second, ACKs would reach
  // the device seconds late, and it would stop sending (then the stream
  // watchdog disconnects). ACKs are a plain byte stream, so a single write
  // carrying N bytes is identical on the wire to N one-byte writes: keep at
  // most ONE ACK write in flight and let every ACK that arrives meanwhile
  // ride in the next one. Queue depth is bounded no matter how slow the
  // link is.
  private pendingAckBytes: number[] = [];
  private ackWriteInFlight = false;
  private ackGeneration = 0;
  private static readonly MAX_PENDING_ACK_BYTES = 4096;

  private queueAck(char: string): void {
    if (!this.writer || this.ackSuppressDepth > 0) return;
    if (this.pendingAckBytes.length >= Ecg11ManagerService.MAX_PENDING_ACK_BYTES) {
      this.pendingAckBytes.length = 0;
    }
    this.pendingAckBytes.push(char.charCodeAt(0));
    this.flushAcks();
  }

  private flushAcks(): void {
    const writer = this.writer;
    if (!writer || this.ackWriteInFlight || this.pendingAckBytes.length === 0) return;
    const bytes = new Uint8Array(this.pendingAckBytes);
    this.pendingAckBytes = [];
    this.ackWriteInFlight = true;
    const generation = this.ackGeneration;
    const done = () => {
      if (generation !== this.ackGeneration) return;
      this.ackWriteInFlight = false;
      this.flushAcks();
    };
    writer.write(bytes).then(done, done);
  }

  private resetAcks(): void {
    this.ackGeneration++;
    this.pendingAckBytes = [];
    this.ackWriteInFlight = false;
  }

  private async writeCommand(char: string): Promise<void> {
    if (!this.writer) return;
    try {
      await withTimeout(
        this.writer.write(new TextEncoder().encode(char)),
        WRITE_TIMEOUT_MS,
        `write timed out after ${WRITE_TIMEOUT_MS}ms -- the OS accepted the port open but the underlying link isn't actually flowing data (a stale Bluetooth-serial pairing behaves exactly like this)`
      );
    } catch (e) {
      const label = COMMAND_NAMES[char];
      if (label) console.warn(`[Ecg11Manager] write '${char}' (${label}) FAILED:`, this.describeError(e));
    }
  }

  async startStreaming(): Promise<void> {
    if (this.stateSubject.value.connection !== 'connected') {
      this.setError('Not connected to a device.');
      return;
    }
    this.processor.reset();
    this.hasLoggedFirstFrame = false;
    this.hasLoggedFirstNack = false;
    this.hasLoggedFirstBytes = false;
    this.resetAcks();
    this.parser?.reset();
    this.softRestartAttempts = 0;
    this.lastSoftRestartAtMs = 0;
    this.isStreamingFlag = true;
    this.lastValidFrameAtMs = Date.now();
    this.setState({ ...this.stateSubject.value, isStreaming: true, warmedUp: false, errorMessage: null });
    // Sends a FRESH, complete S -> R -> A handshake, not bare 'A' alone --
    // matches Aabo's native Android implementation for this exact device
    // (confirmed against real hardware: a bare 'A' relying on an earlier
    // S/R produced zero frames).
    this.runStartedAtMs = Date.now();
    this.lastProactiveRearmAtMs = 0;
    this.proactiveRearmInFlight = false;
    const myToken = ++this.streamAttemptToken;
    this.ackSuppressDepth++;
    try {
      await this.writeCommand(CMD_STOP);
      if (myToken !== this.streamAttemptToken) return; // superseded by Stop/Disconnect
      await this.writeCommand(CMD_RESET);
      if (myToken !== this.streamAttemptToken) return;
      await this.writeCommand(CMD_START);
    } finally {
      this.ackSuppressDepth = Math.max(0, this.ackSuppressDepth - 1);
    }
    if (myToken !== this.streamAttemptToken) return;
    this.startActivityReporter();
  }

  private async probeBattery(): Promise<void> {
    if (this.isStreamingFlag || this.stateSubject.value.connection !== 'connected') return;
    const myToken = ++this.streamAttemptToken;
    this.batteryProbeActive = true;
    this.resetAcks();
    this.parser?.reset();
    try {
      await this.writeCommand(CMD_START);
      const startedAt = Date.now();
      while (
        myToken === this.streamAttemptToken &&
        this.stateSubject.value.batteryPercent === null &&
        Date.now() - startedAt < BATTERY_PROBE_TIMEOUT_MS
      ) {
        await this.sleep(100);
      }
      // Superseded by startStreaming()/stopStreaming()/disconnect() -- they
      // own the device's acquisition state now, so don't send STOP here.
      if (myToken !== this.streamAttemptToken) return;
      if (this.stateSubject.value.batteryPercent === null) {
        console.warn('[Ecg11Manager] Battery probe: no battery reading received within timeout');
      }
      this.resetAcks();
      await this.writeCommand(CMD_STOP);
      await this.writeCommand(CMD_RESET);
      this.parser?.reset();
    } finally {
      this.batteryProbeActive = false;
    }
  }

  stopStreaming(): void {
    this.streamAttemptToken++; // cancel any in-flight startStreaming() tail
    this.isStreamingFlag = false;
    this.resetAcks();
    this.setState({ ...this.stateSubject.value, isStreaming: false });
    this.writeCommand(CMD_STOP);
    this.stopActivityReporter();
  }

  disconnect(): void {
    this.connectAttemptToken++; // cancel any in-flight connect attempt
    this.isConnecting = false;
    this.isStreamingFlag = false;
    this.resetAcks();
    if (this.port) this.writeCommand(CMD_STOP);
    this.teardown();
  }

  private async teardown(): Promise<void> {
    this.streamAttemptToken++;
    this.stopActivityReporter();
    this.isStreamingFlag = false;
    this.resetAcks();
    this.parser = null;
    try {
      if (this.reader) await withTimeout(this.reader.cancel(), CLOSE_TIMEOUT_MS, 'reader.cancel() timed out');
    } catch { /* already gone */ }
    this.reader = null;
    try { this.writer?.releaseLock(); } catch { /* already released */ }
    this.writer = null;
    const port = this.port;
    this.port = null;
    if (port) {
      // Same stale-pairing hang risk as write()/setSignals() above -- a
      // wedged link can leave close() unresolved too, which would otherwise
      // block every caller awaiting teardown() forever.
      try { await withTimeout(port.close(), CLOSE_TIMEOUT_MS, 'port.close() timed out'); } catch { /* already closed */ }
    }
    this.setState({ ...this.stateSubject.value, connection: 'disconnected', isStreaming: false, warmedUp: false, batteryPercent: null, leadOff: {} });
  }

  private startActivityReporter(): void {
    if (this.activityInterval) return;
    this.activityInterval = setInterval(() => this.activityReporterTick(), 1000);
  }

  private activityReporterTick(): void {
    if (!this.isStreamingFlag) return;

    if (Date.now() - this.lastValidFrameAtMs > STREAM_WATCHDOG_TIMEOUT_MS) {
      console.warn(`[Ecg11Manager] No valid frame received in over ${STREAM_WATCHDOG_TIMEOUT_MS / 1000}s while streaming -- disconnecting automatically (matches the native Android app's own watchdog behavior for this exact device).`);
      this.setError(`Device stopped responding: no data for over ${STREAM_WATCHDOG_TIMEOUT_MS / 1000}s. Disconnected automatically. Power-cycle the device, then Connect again.`);
      this.disconnect();
      return;
    }

    const silentMs = Date.now() - this.lastValidFrameAtMs;
    this.maybeSendProactiveRearm(silentMs);
    if (
      silentMs > SOFT_RESTART_AFTER_MS &&
      this.softRestartAttempts < MAX_SOFT_RESTARTS &&
      !this.softRestartInFlight &&
      Date.now() - this.lastSoftRestartAtMs > SOFT_RESTART_RETRY_GAP_MS
    ) {
      void this.softRestartStream(silentMs);
    }

    if (this.bytesThisWindow === 0) {
      this.consecutiveZeroByteWindows++;
      if (this.consecutiveZeroByteWindows === 5 && !this.hasLoggedZeroByteHint) {
        this.hasLoggedZeroByteHint = true;
        // Confirmed fix (both platforms): forget/unpair the device in the
        // OS's Bluetooth settings, then re-pair it fresh -- reconnecting
        // without forgetting it first does not help.
        console.warn('[Ecg11Manager] HINT: 0 bytes arriving for 5s straight usually means the OS has a stale Bluetooth pairing for this device -- it can accept the connection and commands without any error, but never actually deliver data. Confirmed fix: go to your OS\'s Bluetooth settings, FORGET/unpair this device entirely, then re-pair it fresh, then Connect again. (On Windows, a Bluetooth-serial device shows up as two COM ports -- open Settings > Bluetooth & devices > [device] > More Bluetooth options > COM Ports tab and use the one marked "Outgoing", not "Incoming". On Mac, the picker offers two entries -- e.g. "ECGREC-232401-278" and "cu.ECGREC-232401-278" -- pick the one starting with "cu.". Either way, try forgetting/re-pairing first -- a stale pairing can still block data even on the right port.)');
      }
    } else if (this.framesThisWindow === 0) {
      this.consecutiveZeroByteWindows = 0;
      this.hasLoggedZeroByteHint = false;
    } else {
      this.consecutiveZeroByteWindows = 0;
      this.hasLoggedZeroByteHint = false;
    }
    this.framesThisWindow = 0;
    this.nacksThisWindow = 0;
    this.bytesThisWindow = 0;
  }

  private maybeSendProactiveRearm(silentMs: number): void {
    if (
      this.proactiveRearmInFlight ||
      this.softRestartInFlight ||
      this.runStartedAtMs === 0 ||
      silentMs >= SOFT_RESTART_AFTER_MS ||
      Date.now() - Math.max(this.runStartedAtMs, this.lastProactiveRearmAtMs) < PROACTIVE_REARM_MS
    ) return;
    void this.sendProactiveRearm();
  }

  private async sendProactiveRearm(): Promise<void> {
    this.proactiveRearmInFlight = true;
    const token = this.streamAttemptToken;
    this.ackSuppressDepth++;
    try {
      await this.writeCommand(CMD_START);
    } finally {
      this.ackSuppressDepth = Math.max(0, this.ackSuppressDepth - 1);
      this.proactiveRearmInFlight = false;
    }
    if (token !== this.streamAttemptToken || !this.isStreamingFlag) return; // superseded by Stop/Disconnect/soft restart
    this.lastProactiveRearmAtMs = Date.now();
  }

  private async softRestartStream(silentMs: number): Promise<void> {
    this.softRestartInFlight = true;
    this.softRestartAttempts++;
    this.lastSoftRestartAtMs = Date.now();
    const token = ++this.streamAttemptToken;
    try {
      console.warn(`[Ecg11Manager] No valid frame for ${Math.round(silentMs / 1000)}s -- restarting acquisition in place (attempt ${this.softRestartAttempts}/${MAX_SOFT_RESTARTS}) before giving up.`);
      this.parser?.reset();
      this.resetAcks();
      await this.writeCommand(CMD_STOP);
      if (token !== this.streamAttemptToken) return;
      await this.writeCommand(CMD_RESET);
      if (token !== this.streamAttemptToken) return;
      await this.writeCommand(CMD_START);
    } finally {
      this.softRestartInFlight = false;
    }
  }

  private stopActivityReporter(): void {
    if (this.activityInterval) {
      clearInterval(this.activityInterval);
      this.activityInterval = null;
    }
    this.framesThisWindow = 0;
    this.nacksThisWindow = 0;
    this.bytesThisWindow = 0;
    this.hasLoggedFirstFrame = false;
    this.hasLoggedFirstNack = false;
    this.hasLoggedFirstBytes = false;
    this.consecutiveZeroByteWindows = 0;
    this.hasLoggedZeroByteHint = false;
  }

  private updateBattery(status3: number): void {
    const level = Math.min(100, (status3 & 0x0f) * 7);
    // A computed level of 0 isn't a real reading -- hold the last real
    // reading instead of flashing the display down to 0%.
    if (level > 0 && level !== this.stateSubject.value.batteryPercent) {
      this.setState({ ...this.stateSubject.value, batteryPercent: level });
    }
  }

  private describeError(err: unknown): string {
    if (err instanceof DOMException) return `${err.message} (${err.name})`;
    if (err instanceof Error) return err.message;
    return String(err);
  }

  private friendlyConnectError(err: unknown): string {
    const raw = this.describeError(err);
    if (err instanceof DOMException) {
      if (err.name === 'NetworkError') {
        return `Could not open the device: the OS reports it's not available right now (${raw}). Check the device is powered on, in range, and not already connected elsewhere.`;
      }
      if (err.name === 'InvalidStateError') {
        return `Could not open the device: it's already open elsewhere (${raw}). Close any other tab/app using it and try again.`;
      }
      if (err.name === 'SecurityError') {
        return `Could not open the device: permission wasn't granted (${raw}). Try Connect again and allow access when the browser asks.`;
      }
    }
    return `Could not open the device: ${raw}`;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private setError(message: string): void {
    console.warn('[Ecg11Manager]', message);
    this.setState({ ...this.stateSubject.value, errorMessage: message });
  }

  private setState(state: Ecg11State): void {
    this.stateSubject.next(state);
  }
}
